import { useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import StandardModal from '@/components/common/standard.modal';
import { useTranslations } from '@/i18n/use-translations';
import type {
  ContributorRoleSlotSummaryDto,
  ContributorRoleSummaryDto,
  ContributorSummaryDto,
} from '@/types/dtos/contributors/response/contributor-summary.dto';
import type { EventSummaryDto } from '@/types/dtos/events/response/event-summary.dto';
import type { FlowSummaryDto } from '@/types/dtos/flows/response/flow-summary.dto';
import type { SessionSummaryDto } from '@/types/dtos/sessions/response/session-summary.dto';
import { usePatchContributorRoleMutation } from '@/state/mutations/contributors/use-patch-contributor-role';
import { usePatchContributorRoleSlotMutation } from '@/state/mutations/contributors/use-patch-contributor-role-slot';
import { useNotifications } from '@/hooks/use-notifications';
import { TermsAndPrivacyCheckboxes } from './terms-and-privacy-checkboxes';
import { TermsModal } from './terms-modal';
import { useGetPublishedVersionsQuery } from '@/state/queries/events/use-get-terms';
import { TERM_TYPE } from '@/types/dtos/events/term.dto';
import { slotNeedsRemoteSpeakerConfirmation } from '@/utils/remote-speaker-status.utils';
import type { EventSlotDto } from '@/types/dtos/events/response/event.dto';
import type { FlowSlotSummaryDto } from '@/types/dtos/flows/response/flow-summary.dto';
import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';
import { PhoneCallbackOutlined } from '@mui/icons-material';

export type ConfirmAllModalMode = 'all' | 'selected';

type PendingSlotAction = {
  slot: ContributorRoleSlotSummaryDto;
  kind: 'accept' | 'remote';
};

type PendingRolePreview = {
  role: ContributorRoleSummaryDto;
  acceptRole: boolean;
  slots: PendingSlotAction[];
};

interface ConfirmAllModalProps {
  open: boolean;
  onClose: () => void;
  contributor: ContributorSummaryDto;
  event?: EventSummaryDto;
  flows?: FlowSummaryDto[];
  sessions?: SessionSummaryDto[];
  mode: ConfirmAllModalMode;
  selectedRoleIds?: Set<string>;
  selectedSlotIds?: Map<string, Set<string>>;
  remoteSpeakershipEnabled?: boolean;
  onSuccess?: () => void;
}

const ConfirmAllModal = ({
  open,
  onClose,
  contributor,
  event,
  flows = [],
  sessions = [],
  mode,
  selectedRoleIds,
  selectedSlotIds,
  remoteSpeakershipEnabled = false,
  onSuccess,
}: ConfirmAllModalProps) => {
  const t = useTranslations();
  const notification = useNotifications();
  const [acceptTermsChecked, setAcceptTermsChecked] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const { mutateAsync: patchContributorRoleMutation, isPending: isPatchContributorRolePending } =
    usePatchContributorRoleMutation();
  const { mutateAsync: patchContributorRoleSlotMutation, isPending: isPatchContributorRoleSlotPending } =
    usePatchContributorRoleSlotMutation();

  const flowsById = useMemo(() => new Map(flows.map((flow) => [flow.id, flow])), [flows]);
  const sessionsById = useMemo(() => new Map(sessions.map((session) => [session.id, session])), [sessions]);

  const eventId = event?.id || contributor.event?.id;

  const { data: publishedTermsData } = useGetPublishedVersionsQuery(eventId, TERM_TYPE.CONTRIBUTION_TERMS, {
    enabled: !!eventId,
  });

  const latestTerm = useMemo(() => {
    if (!publishedTermsData?.terms || publishedTermsData.terms.length === 0) {
      return null;
    }
    return publishedTermsData.terms[publishedTermsData.terms.length - 1];
  }, [publishedTermsData]);

  const hasAcceptedTerms = useMemo(() => {
    if (!contributor.acceptedTerms || contributor.acceptedTerms.length === 0) {
      return false;
    }
    return contributor.acceptedTerms.some((term) => term.type === TERM_TYPE.CONTRIBUTION_TERMS);
  }, [contributor.acceptedTerms]);

  const showTermsCheckbox = !!latestTerm && !hasAcceptedTerms;

  useEffect(() => {
    if (open) {
      queueMicrotask(() => {
        setAcceptTermsChecked(false);
        setTermsModalOpen(false);
      });
    }
  }, [open]);

  const getRoleEntityTitle = useCallback(
    (role: ContributorRoleSummaryDto) => {
      if (role.entityType === 'Event') {
        return event?.title || t('myEventsList.unknown');
      }
      if (!role.entityId) return t('myEventsList.unknown');
      if (role.entityType === 'Session') {
        return sessionsById.get(role.entityId)?.title || t('myEventsList.unknown');
      }
      if (role.entityType === 'Flow') {
        return flowsById.get(role.entityId)?.title || t('myEventsList.unknown');
      }
      return t('myEventsList.unknown');
    },
    [event?.title, flowsById, sessionsById, t],
  );

  const findEventSlot = useCallback(
    (eventSlotId: string | undefined) => {
      if (!event || !eventSlotId) return undefined;
      return event.slots?.find((slot) => slot.id === eventSlotId || slot.origin === eventSlotId);
    },
    [event],
  );

  const getFlowSlotTimingSummary = (
    currentEvent: EventSummaryDto,
    flowSlotInEvent: EventSlotDto,
    contentSlotInFlow: FlowSlotSummaryDto,
  ) => {
    if (!flowSlotInEvent.day || !currentEvent.days) {
      return t('myEventsList.contributionsList.slotWithoutDescription');
    }
    const day = currentEvent.days.find((d) => d.id === flowSlotInEvent.day.id);
    if (!day) return t('myEventsList.contributionsList.slotWithoutDescription');
    const dayNumber = currentEvent.days.indexOf(day) + 1;
    if (!currentEvent.startDate) return `Day ${dayNumber}`;
    const slotOffsetMinutes =
      contentSlotInFlow?.computedOffsetMinutes ?? contentSlotInFlow?.plannedOffsetMinutes ?? null;
    if (slotOffsetMinutes === null) return `Day ${dayNumber}`;
    const startingOffsetInDay = flowSlotInEvent.startOffsetMinutes;
    const slotStartOffsetFromEventStart = startingOffsetInDay + slotOffsetMinutes;
    const slotStartTime = minuteOffsetToTime(slotStartOffsetFromEventStart, currentEvent.startDate);
    const slotDuration = contentSlotInFlow?.computedDurationMinutes ?? contentSlotInFlow?.plannedDurationMinutes;
    if (slotDuration === undefined) return `Day ${dayNumber} | ${slotStartTime}`;
    const slotEndTime = minuteOffsetToTime(slotStartOffsetFromEventStart + slotDuration, currentEvent.startDate);
    return `Day ${dayNumber} | ${slotStartTime} - ${slotEndTime}`;
  };

  const getRoleSlotTimingText = (roleSlot: ContributorRoleSlotSummaryDto) => {
    if (!event?.slots || !event.days) {
      return t('myEventsList.contributionsList.slotWithoutDescription');
    }

    if (roleSlot.entityType === 'FlowSlot') {
      const entityId = roleSlot.entityId;
      if (!entityId) return t('myEventsList.contributionsList.slotWithoutDescription');
      const flowId = roleSlot.parentEntity;
      const flow = flowId ? flowsById.get(flowId) : undefined;
      const contentSlotInFlow = flow?.slots?.find((slot) => slot.id === entityId || slot.origin === entityId);
      const flowSlotInEvent =
        findEventSlot(roleSlot.eventSlotId) ||
        (flowId ? event.slots.find((slot) => slot.content === flowId || slot.origin === flowId) : undefined) ||
        event.slots.find((slot) => slot.content === entityId || slot.origin === entityId);

      if (flowSlotInEvent && contentSlotInFlow) {
        return getFlowSlotTimingSummary(event, flowSlotInEvent, contentSlotInFlow);
      }
      return t('myEventsList.contributionsList.slotWithoutDescription');
    }

    if (roleSlot.entityType === 'EventSlot') {
      const eventSlot = findEventSlot(roleSlot.entityId) || findEventSlot(roleSlot.eventSlotId);
      if (!eventSlot?.day || typeof eventSlot.startOffsetMinutes !== 'number') {
        return t('myEventsList.contributionsList.slotWithoutDescription');
      }
      const dayIndex = event.days.findIndex((day) => day.id === eventSlot.day.id);
      if (dayIndex === -1) return t('myEventsList.contributionsList.slotWithoutDescription');
      const dayNumber = dayIndex + 1;
      const startTime = minuteOffsetToTime(eventSlot.startOffsetMinutes, event.startDate);
      const durationMinutes = eventSlot.durationPlannedMinutes;
      if (!durationMinutes) return `Day ${dayNumber} | ${startTime}`;
      const endTime = minuteOffsetToTime(eventSlot.startOffsetMinutes + durationMinutes, event.startDate);
      return `Day ${dayNumber} | ${startTime} - ${endTime}`;
    }

    if (roleSlot.entityType === 'Flow') {
      const flowId = roleSlot.entityId;
      if (!flowId) return t('myEventsList.contributionsList.slotWithoutDescription');
      const flow = flowsById.get(flowId);
      if (!flow) return t('myEventsList.contributionsList.slotWithoutDescription');
      return flow.title || t('myEventsList.contributionsList.slotWithoutDescription');
    }

    return t('myEventsList.contributionsList.slotWithoutDescription');
  };

  const getRoleSlotTitle = useCallback(
    (roleSlot: ContributorRoleSlotSummaryDto) => {
      if (roleSlot.entityType !== 'FlowSlot') return undefined;
      const entityId = roleSlot.entityId;
      if (!entityId) return undefined;
      const flowId = roleSlot.parentEntity;
      const flow = flowId ? flowsById.get(flowId) : undefined;
      if (flow?.title) return flow.title;
      const contentSlotInFlow = flow?.slots?.find((slot) => slot.id === entityId || slot.origin === entityId);
      if (!contentSlotInFlow) return undefined;
      if (contentSlotInFlow.title) return contentSlotInFlow.title;
      const content = contentSlotInFlow.content;
      if (typeof content === 'string') {
        return sessionsById.get(content)?.title;
      }
      return content?.title;
    },
    [flowsById, sessionsById],
  );

  const pendingPreview = useMemo((): PendingRolePreview[] => {
    const result: PendingRolePreview[] = [];

    for (const role of contributor.roles || []) {
      const selectedSlots = selectedSlotIds?.get(role.id);
      const roleSelected = selectedRoleIds?.has(role.id) ?? false;

      const slotActions: PendingSlotAction[] = [];
      for (const slot of role.slots ?? []) {
        const needsAccept = !slot.acceptedAt;
        const needsRemote = remoteSpeakershipEnabled && slotNeedsRemoteSpeakerConfirmation(slot);

        // Selected mode: only the explicitly checked slot ids — never expand to sibling slots.
        if (mode === 'selected' && !selectedSlots?.has(slot.id)) {
          continue;
        }

        if (!needsAccept && !needsRemote) continue;

        if (needsAccept) {
          slotActions.push({ slot, kind: 'accept' });
        } else if (needsRemote) {
          slotActions.push({ slot, kind: 'remote' });
        }
      }

      // Role-only acceptance when explicitly selected (or confirm-all) and no slot patches are queued.
      // Accepting slots auto-accepts the role on the backend.
      const showRoleOnly = !role.acceptedAt && slotActions.length === 0 && (mode === 'all' || roleSelected);

      if (showRoleOnly || slotActions.length > 0) {
        result.push({
          role,
          acceptRole: showRoleOnly,
          slots: slotActions,
        });
      }
    }

    return result;
  }, [contributor.roles, mode, remoteSpeakershipEnabled, selectedRoleIds, selectedSlotIds]);

  const handleConfirm = useCallback(async () => {
    if (showTermsCheckbox && !acceptTermsChecked) return;
    if (pendingPreview.length === 0) {
      onClose();
      return;
    }

    const now = new Date().toISOString();
    const acceptedTerms =
      latestTerm && acceptTermsChecked
        ? [
            {
              id: latestTerm.id,
              type: latestTerm.type,
              version: latestTerm.version,
              text: latestTerm.text,
              acceptedAt: now,
            },
          ]
        : undefined;

    const allPromises: Promise<unknown>[] = [];

    for (const entry of pendingPreview) {
      const { role, acceptRole, slots } = entry;

      if (slots.length > 0) {
        for (const { slot, kind } of slots) {
          if (kind === 'accept') {
            allPromises.push(
              patchContributorRoleSlotMutation({
                contributorId: contributor.id,
                roleId: role.id,
                slotId: slot.id,
                acceptedAt: now,
                acceptedTerms,
              }),
            );
          } else {
            allPromises.push(
              patchContributorRoleSlotMutation({
                contributorId: contributor.id,
                roleId: role.id,
                slotId: slot.id,
                remoteSpeakerAcceptedAt: now,
                acceptedTerms,
              }),
            );
          }
        }
      } else if (acceptRole) {
        allPromises.push(
          patchContributorRoleMutation({
            contributorId: contributor.id,
            roleId: role.id,
            acceptedAt: now,
            acceptedTerms,
          }),
        );
      }
    }

    if (allPromises.length === 0) {
      onClose();
      return;
    }

    const results = await Promise.allSettled(allPromises);
    const failedResults = results.filter((r) => r.status === 'rejected');

    if (failedResults.length === 0) {
      onClose();
      notification.notifySuccess(t('common.saved'));
      onSuccess?.();
    } else {
      const firstError = failedResults[0]?.status === 'rejected' ? failedResults[0].reason : null;
      if (
        firstError &&
        typeof firstError === 'object' &&
        'message' in firstError &&
        String(firstError.message).includes('Forbidden')
      ) {
        notification.notifyError(t('user.contributionsList.errorPermissionDenied'));
      } else {
        notification.notifyError(t('user.contributionsList.errorAcceptRole'));
      }
    }
  }, [
    showTermsCheckbox,
    acceptTermsChecked,
    pendingPreview,
    contributor.id,
    patchContributorRoleMutation,
    patchContributorRoleSlotMutation,
    notification,
    t,
    onClose,
    onSuccess,
    latestTerm,
  ]);

  const isSubmitting = isPatchContributorRolePending || isPatchContributorRoleSlotPending;
  const title =
    mode === 'selected'
      ? t('user.contributionsList.confirmSelectedContributions')
      : t('user.contributionsList.confirmAllContributions');
  const message =
    mode === 'selected'
      ? t('user.contributionsList.confirmSelectedContributionsMessage', {
          eventTitle: event?.title || t('myEventsList.unknown'),
        } as Record<string, string>)
      : t('user.contributionsList.confirmAllContributionsMessage', {
          eventTitle: event?.title || t('myEventsList.unknown'),
        } as Record<string, string>);

  return (
    <StandardModal open={open} close={onClose} title={title} menu={false} size="md">
      <Stack spacing={2} sx={{ py: 2 }}>
        <Typography variant="body1">{message}</Typography>

        {pendingPreview.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('user.contributionsList.confirmPreviewTitle')}
            </Typography>
            <Box
              sx={{
                maxHeight: 320,
                overflow: 'auto',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                px: 1.5,
                py: 1,
              }}
            >
              <Stack spacing={2} divider={<Divider flexItem />}>
                {pendingPreview.map(({ role, acceptRole, slots }) => (
                  <Stack key={role.id} spacing={0.5}>
                    <Typography variant="body2" fontWeight={600}>
                      {t(`common.roleLabels.${role.role}`)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getRoleEntityTitle(role)}
                      {acceptRole ? ` · ${t('user.contributionsList.confirmPreviewRoleOnly')}` : ''}
                    </Typography>
                    {slots.length > 0 && (
                      <Stack spacing={0.25} sx={{ pl: 1 }}>
                        {slots.map(({ slot, kind }) => {
                          const timing = getRoleSlotTimingText(slot);
                          const slotTitle = getRoleSlotTitle(slot);
                          const slotLabel = [timing, slotTitle].filter(Boolean).join(' · ');
                          const line =
                            kind === 'remote'
                              ? t('user.contributionsList.speakingRemotelyAt', {
                                  slot: slotLabel,
                                } as Record<string, string>)
                              : slotLabel;
                          const confirmsRemoteWithSlot =
                            kind === 'accept' && remoteSpeakershipEnabled && slot.isRemoteSpeaker === true;
                          return (
                            <Stack key={slot.id} direction="row" spacing={0.5} alignItems="center" component="span">
                              <Typography variant="caption" color="text.secondary">
                                {line}
                              </Typography>
                              {confirmsRemoteWithSlot ? (
                                <PhoneCallbackOutlined color="primary" sx={{ fontSize: 16 }} />
                              ) : null}
                            </Stack>
                          );
                        })}
                      </Stack>
                    )}
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Box>
        )}

        <TermsAndPrivacyCheckboxes
          acceptTermsChecked={acceptTermsChecked}
          onAcceptTermsChange={setAcceptTermsChecked}
          onTermsLinkClick={() => setTermsModalOpen(true)}
          showTermsCheckbox={showTermsCheckbox}
        />
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 2 }}>
          <Button variant="outlined" color="primary" onClick={onClose} disabled={isSubmitting} size="small">
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            loading={isSubmitting}
            disabled={pendingPreview.length === 0 || (showTermsCheckbox && !acceptTermsChecked)}
            size="small"
          >
            {t('common.confirm')}
          </Button>
        </Stack>
      </Stack>
      <TermsModal open={termsModalOpen} onClose={() => setTermsModalOpen(false)} term={latestTerm} />
    </StandardModal>
  );
};

export default ConfirmAllModal;
